import { useEffect, useState, useCallback } from 'react';
import { Config, SummaryData, SummaryFormat, Preset } from '../types';
import { fetchConfig, fetchContext, fetchSummaries } from '../api';
import { useWeavableAuth } from './useWeavableAuth';

export { WeavableAuthContext, useWeavableAuth } from './useWeavableAuth';

/**
 * Hook to fetch config
 */
export function useConfig() {
    const { baseUrl, connectionId, getToken, onError } = useWeavableAuth();
    const [config, setConfig] = useState<Config | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetch = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const token = await getToken();
            const data = await fetchConfig(baseUrl, connectionId, token);
            setConfig(data);
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to fetch config');
            setError(error);
            onError?.(error);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, connectionId, getToken, onError]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    return { config, loading, error, refetch: fetch };
}

/**
 * Hook to fetch context ID
 */
export function useContextId(fromDateIso: string, timeZone: string, enabled: boolean) {
    const { baseUrl, connectionId, getToken, onError } = useWeavableAuth();
    const [contextId, setContextId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetch = useCallback(async () => {
        if (!enabled) return;

        setLoading(true);
        setError(null);

        try {
            const token = await getToken();
            const data = await fetchContext(baseUrl, connectionId, fromDateIso, timeZone, token);
            setContextId(data.id);
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to fetch context');
            setError(error);
            onError?.(error);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, connectionId, fromDateIso, timeZone, getToken, onError, enabled]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    return { contextId, loading, error, refetch: fetch };
}

/**
 * Hook to fetch summaries for all enabled presets
 */
export function useSummaries(
    contextId: string | null,
    presets: Preset[],
    format: SummaryFormat,
    enabled: boolean,
    fromDateIso: string,
    onSummariesLoaded?: (params: { fromDate: Date; toDate: Date }) => void,
) {
    const { baseUrl, getToken, onError } = useWeavableAuth();
    const [summaries, setSummaries] = useState<SummaryData[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetch = useCallback(async () => {
        if (!enabled || !contextId || presets.length === 0) return;

        setLoading(true);
        setError(null);

        try {
            const token = await getToken();
            const enabledPresets = presets.filter((p) => p.enabled);
            const presetIds = enabledPresets.map((p) => p.id);
            const toDate = new Date();

            const data = await fetchSummaries(baseUrl, contextId, presetIds, format, token);

            // Map responses back to presets
            const summaryMap = new Map(data.summaries.map((s) => [s.preset, s]));
            const summaries: SummaryData[] = enabledPresets.map((preset) => {
                const summary = summaryMap.get(preset.id);
                return {
                    preset,
                    content: summary?.summary ?? null,
                    format: summary?.format ?? format,
                    loading: false,
                    error: null,
                };
            });

            // only set summaries that have content
            setSummaries(summaries.filter((s) => s.content != null && s.content.trim() !== ''));

            // Call the callback after successfully loading summaries
            if (onSummariesLoaded) {
                const fromDate = new Date(fromDateIso);
                onSummariesLoaded({ fromDate, toDate });
            }
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to fetch summaries');
            setError(error);
            onError?.(error);

            // Set error state for all summaries
            setSummaries(
                presets.map((preset) => ({
                    preset,
                    content: null,
                    format,
                    loading: false,
                    error,
                })),
            );
        } finally {
            setLoading(false);
        }
    }, [baseUrl, contextId, presets, format, getToken, onError, enabled, fromDateIso, onSummariesLoaded]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    return { summaries, loading, error, refetch: fetch };
}

// Re-export analytics hooks
export { useAnalyticsConfig, useAnalytics, useAllAnalytics } from './useAnalytics';
